import type { Timestamp } from "@bufbuild/protobuf";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { AlertTriangle, CreditCard, Gift, Loader2 } from "lucide-react";
import { useState } from "react";
import { useOrganization } from "../../gen/rbt/cloud/v1alpha1/organization/organization_rbt_react";
import { PaymentForm } from "../PaymentForm";

// Format a cents amount (as a `bigint`) as a dollar string, e.g.
// `10000n` -> "$100.00".
function formatCents(cents: bigint): string {
  const dollars = Number(cents) / 100;
  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

// Format a protobuf `Timestamp` as a date, e.g. "September 27, 2026".
// Sub-second precision is carried through even though a date never
// shows it, so that this rounds the same way a time-of-day formatter
// would.
function formatTimestamp(timestamp: Timestamp): string {
  const milliseconds = Number(timestamp.seconds) * 1000 + timestamp.nanos / 1e6;

  return new Date(milliseconds).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

interface BillingSectionProps {
  organizationId: string;
  currentUserId: string;
}

export function BillingSection({ organizationId }: BillingSectionProps) {
  // Reboot hooks must come before React hooks.
  // ISSUE: https://github.com/reboot-dev/mono/issues/5197
  const { useSubscription, updateCard, redeemCreditCode } = useOrganization({
    id: organizationId,
  });
  const { response: subscriptionResponse } = useSubscription(
    {},
    { suspense: true }
  );

  // React hooks come after all Reboot hooks.
  const { toast } = useToast();
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [creditCode, setCreditCode] = useState("");
  const [isRedeeming, setIsRedeeming] = useState(false);

  const subscription = subscriptionResponse!.subscription;
  const card = subscription?.card;
  const billingExempt = subscriptionResponse!.billingExempt;

  // Stripe has told us this organization owes us money. Its
  // applications keep running until the grace period runs out.
  const inGracePeriod = subscription?.gracePeriodStartedAt !== undefined;
  const gracePeriodExpiresAt = subscription?.gracePeriodExpiresAt;
  // A grace period can outlast a billing cycle, so more than one
  // invoice may be waiting to be paid.
  const openInvoices = subscription?.openInvoices ?? [];
  const amountDueCents = openInvoices.reduce(
    (total, invoice) => total + invoice.amountDueCents,
    0n
  );
  const payableInvoices = openInvoices.filter((invoice) => invoice.url);

  const creditBalanceCents = subscription?.creditBalanceCents ?? 0n;
  const hasCredit = creditBalanceCents > 0n;
  // Credits were granted but are now exhausted, so normal billing has
  // resumed.
  const creditsDepleted =
    !hasCredit && (subscription?.creditEverGranted ?? false);

  const handleRedeemCreditCode = async () => {
    const code = creditCode.trim();
    if (!code) {
      return;
    }

    setIsRedeeming(true);

    const { aborted } = await redeemCreditCode({ code });

    setIsRedeeming(false);

    if (aborted) {
      toast({
        title: "Could not redeem code",
        description: aborted.message || "Failed to redeem credit code.",
        variant: "destructive",
      });
      return;
    }

    setCreditCode("");
    toast({
      title: "Credit code redeemed",
      description: "Your cloud credit is being applied to your account.",
    });
  };

  // Capitalize the first letter of the card brand.
  const displayBrand = card
    ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1)
    : undefined;

  const handleOpenDialog = async () => {
    setShowUpdateDialog(true);

    const { aborted } = await updateCard({});

    if (aborted) {
      toast({
        title: "Error",
        description: aborted.message || "Failed to start payment update.",
        variant: "destructive",
      });
      setShowUpdateDialog(false);
      return;
    }
  };

  const handlePaymentSuccess = () => {
    toast({
      title: "Payment method updated",
      description: "Your new card has been saved.",
    });
    setShowUpdateDialog(false);
  };

  const handlePaymentError = (message: string) => {
    toast({
      title: "Payment Error",
      description: message,
      variant: "destructive",
    });
  };

  const handleCloseDialog = (open: boolean) => {
    if (!open) {
      setShowUpdateDialog(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Billing</CardTitle>
        <CardDescription>
          Manage your payment method for Reboot Cloud.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {inGracePeriod && (
          <div className="mb-6 rounded-md border border-destructive/50 bg-destructive/10 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-destructive">
                    Payment failed &mdash; {formatCents(amountDueCents)} due
                    {openInvoices.length > 1 &&
                      ` across ${openInvoices.length} invoices`}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    We were not able to collect payment for this organization.
                    Your applications are still running
                    {gracePeriodExpiresAt !== undefined
                      ? `, but on ${formatTimestamp(
                          gracePeriodExpiresAt
                        )} they will be taken down. Taking an application down
                         deletes it and its data, and cannot be undone.`
                      : "."}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Pay what is outstanding, or update your payment method below
                    &mdash; updating it also retries every unpaid invoice.
                    Redeeming a credit code that covers the balance settles it
                    too.
                  </p>
                </div>
                {payableInvoices.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {payableInvoices.map((invoice) => (
                      <Button key={invoice.id} asChild variant="default">
                        <a
                          href={invoice.url}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          {payableInvoices.length > 1
                            ? `Pay ${formatCents(invoice.amountDueCents)}`
                            : "Pay now"}
                        </a>
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {billingExempt ? (
          <div className="flex items-center gap-3">
            <CreditCard className="h-5 w-5 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              This organization is billed internally. No payment method is
              required.
            </p>
          </div>
        ) : card ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">
                  {displayBrand} ending in {card.last4}
                </p>
                <p className="text-sm text-muted-foreground">
                  Expires {String(card.expMonth).padStart(2, "0")}/
                  {card.expYear}
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={handleOpenDialog}>
              Update Payment Method
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              No payment method on file.
            </p>
            <Button variant="outline" onClick={handleOpenDialog}>
              Add Payment Method
            </Button>
          </div>
        )}

        <div className="mt-6 border-t pt-6">
          <div className="flex items-center gap-3">
            <Gift className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">Cloud credits</p>
              {hasCredit ? (
                <p className="text-sm text-muted-foreground">
                  {formatCents(creditBalanceCents)} remaining. Credits are
                  applied automatically until used up, after which your payment
                  method is charged.
                </p>
              ) : creditsDepleted ? (
                <p className="text-sm text-muted-foreground">
                  Your cloud credits are used up. Your payment method will now
                  be charged for continued usage.
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No credits yet. Redeem a code below to add cloud credit.
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <Input
              placeholder="Enter credit code"
              value={creditCode}
              onChange={(event) => setCreditCode(event.target.value)}
              disabled={isRedeeming}
              className="max-w-xs"
            />
            <Button
              variant="outline"
              onClick={handleRedeemCreditCode}
              disabled={isRedeeming || !creditCode.trim()}
            >
              {isRedeeming ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Redeeming...
                </>
              ) : (
                "Redeem"
              )}
            </Button>
          </div>
        </div>
      </CardContent>

      <Dialog open={showUpdateDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Update Payment Method</DialogTitle>
            <DialogDescription>
              Enter your new card details below.
            </DialogDescription>
          </DialogHeader>
          {!subscription?.clientSecret ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">
                Preparing payment form...
              </span>
            </div>
          ) : (
            <PaymentForm
              clientSecret={subscription.clientSecret}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              submitLabel="Update card"
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
